<template>
  <div class="psy-page">
    <LandingNav />

    <!-- ═══════════════════════════════════════════
         HERO
    ═══════════════════════════════════════════ -->
    <section class="psy-hero">
      <div class="psy-hero-bg">
        <div class="psy-hero-plate" aria-hidden="true"></div>
        <div class="psy-orb psy-orb-1"></div>
        <div class="psy-orb psy-orb-2"></div>
        <div class="psy-orb psy-orb-3"></div>
        <div class="psy-scanline"></div>
      </div>

      <div class="psy-hero-inner">
        <div class="psy-eyebrow">
          <span class="psy-dot"></span>
          {{ $t('landing_psy.eyebrow') }}
          <span class="psy-dot"></span>
        </div>

        <h1 class="psy-hero-title" :style="{ '--word-chars': heroWordChars }">
          <span class="psy-line-1">{{ $t('landing_psy.hero_line_1') }}</span>
          <span class="psy-line-2">{{ $t('landing_psy.hero_line_2') }}</span>
        </h1>

        <p class="psy-hero-sub">
          {{ $t('landing_psy.hero_sub_1') }}<br />
          {{ $t('landing_psy.hero_sub_2') }}
        </p>

        <div class="psy-hero-ctas">
          <NuxtLink :to="localePath('/auth') + '?tab=signup'" class="psy-btn-primary">
            {{ $t('landing_psy.cta_join') }}
          </NuxtLink>
          <a v-if="tenant?.subscriptionsEnabled" href="#plans" class="psy-btn-ghost">{{ $t('landing_psy.cta_plans') }}</a>
        </div>

        <div class="psy-bpm-ticker">
          <span class="psy-bpm-label">BPM</span>
          <span class="psy-bpm-value">{{ bpm }}</span>
        </div>
      </div>

      <!-- Genre strip at hero bottom -->
      <div class="psy-genre-strip">
        <div class="psy-genre-fade psy-genre-fade-l"></div>
        <div class="psy-genre-track">
          <div class="psy-genre-content">
            <span v-for="i in 3" :key="i" class="psy-genre-set">
              <span class="psy-genre-tag">Forest Psy</span>
              <span class="psy-genre-sep">◈</span>
              <span class="psy-genre-tag">Dark Psy</span>
              <span class="psy-genre-sep">◈</span>
              <span class="psy-genre-tag">Progressive</span>
              <span class="psy-genre-sep">◈</span>
              <span class="psy-genre-tag">Full-On</span>
              <span class="psy-genre-sep">◈</span>
              <span class="psy-genre-tag">Hitech</span>
              <span class="psy-genre-sep">◈</span>
              <span class="psy-genre-tag">Twilight</span>
              <span class="psy-genre-sep">◈</span>
              <span class="psy-genre-tag">Nitzhonot</span>
              <span class="psy-genre-sep">◈</span>
              <span class="psy-genre-tag">Psycore</span>
              <span class="psy-genre-sep">◈</span>
              <span class="psy-genre-tag">Ambient</span>
              <span class="psy-genre-sep">◈</span>
              <span class="psy-genre-tag">Downtempo</span>
              <span class="psy-genre-sep">◈</span>
            </span>
          </div>
        </div>
        <div class="psy-genre-fade psy-genre-fade-r"></div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════
         STATS — no cards, raw numbers
    ═══════════════════════════════════════════ -->
    <section class="psy-stats" ref="statsRef">
      <div class="psy-stats-inner">
        <div v-for="(s, i) in stats" :key="i" class="psy-stat">
          <div class="psy-stat-number">
            {{ s.prefix }}<span class="psy-count">{{ statsVisible ? displayVals[i] : 0 }}</span>{{ s.suffix }}
          </div>
          <div class="psy-stat-divider"></div>
          <div class="psy-stat-label">{{ $t(s.labelKey) }}</div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════
         MANIFESTO
    ═══════════════════════════════════════════ -->
    <section class="psy-manifesto">
      <div class="psy-manifesto-inner">
        <div class="psy-manifesto-symbol">⬡</div>
        <blockquote class="psy-quote">
          {{ $t('landing_psy.quote_1') }}<br />
          {{ $t('landing_psy.quote_2') }}
        </blockquote>
        <p class="psy-manifesto-body">
          {{ $t('landing_psy.manifesto') }}
        </p>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════
         FEATURES — vertical bold list, not a grid
    ═══════════════════════════════════════════ -->
    <section class="psy-features">
      <div class="psy-features-header">
        <span class="psy-label-tag">{{ $t('landing_psy.features_label') }}</span>
        <h2 class="psy-section-title">{{ $t('landing_psy.features_title_pre') }}<br /><em>{{ $t('landing_psy.features_title_highlight') }}</em></h2>
      </div>

      <div class="psy-feature-list">
        <div v-for="(f, i) in features" :key="i" class="psy-feature-row" :class="{ reversed: i % 2 !== 0 }">
          <div class="psy-feature-num">{{ String(i + 1).padStart(2, '0') }}</div>
          <div class="psy-feature-body">
            <h3 class="psy-feature-title">{{ $t(`landing_psy.feature_${f.key}_title`) }}</h3>
            <p class="psy-feature-desc">{{ $t(`landing_psy.feature_${f.key}_desc`) }}</p>
          </div>
          <div class="psy-feature-glyph">{{ f.glyph }}</div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════
         PROCESS — 3-step flow
    ═══════════════════════════════════════════ -->
    <section class="psy-process">
      <div class="psy-process-inner">
        <span class="psy-label-tag">{{ $t('landing_psy.process_label') }}</span>
        <div class="psy-steps">
          <div class="psy-step">
            <div class="psy-step-circle">01</div>
            <div class="psy-step-line"></div>
            <h4 class="psy-step-title">{{ $t('landing_psy.step_upload_title') }}</h4>
            <p class="psy-step-desc">{{ $t('landing_psy.step_upload_desc') }}</p>
          </div>
          <div class="psy-step">
            <div class="psy-step-circle">02</div>
            <div class="psy-step-line"></div>
            <h4 class="psy-step-title">{{ $t('landing_psy.step_distribute_title') }}</h4>
            <p class="psy-step-desc">{{ $t('landing_psy.step_distribute_desc') }}</p>
          </div>
          <div class="psy-step last">
            <div class="psy-step-circle">03</div>
            <h4 class="psy-step-title">{{ $t('landing_psy.step_earn_title') }}</h4>
            <p class="psy-step-desc">{{ $t('landing_psy.step_earn_desc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════
         PRICING anchor — hidden when the tenant has no Stripe
         subscriptions configured, otherwise the "View Plans" CTA
         scrolls visitors to an empty section.
    ═══════════════════════════════════════════ -->
    <div v-if="tenant?.subscriptionsEnabled" id="plans">
      <LandingPricing :showButtons="true" />
    </div>

    <!-- ═══════════════════════════════════════════
         FINAL CTA — full-screen takeover
    ═══════════════════════════════════════════ -->
    <section class="psy-cta">
      <div class="psy-cta-bg">
        <div class="psy-cta-orb"></div>
        <div class="psy-cta-ring psy-ring-1"></div>
        <div class="psy-cta-ring psy-ring-2"></div>
        <div class="psy-cta-ring psy-ring-3"></div>
      </div>
      <div class="psy-cta-inner">
        <p class="psy-cta-pre">{{ $t('landing_psy.final_pre') }}</p>
        <h2 class="psy-cta-title" :style="{ '--word-chars': ctaWordChars }">{{ $t('landing_psy.final_title') }}</h2>
        <p class="psy-cta-sub">{{ $t('landing_psy.final_sub') }}</p>
        <NuxtLink :to="localePath('/auth') + '?tab=signup'" class="psy-btn-primary psy-btn-large">
          {{ $t('landing_psy.cta_join') }}
        </NuxtLink>
      </div>
    </section>

    <AppFooter />
    <GlobalAudioPlayer />
  </div>
</template>

<script setup>
const localePath = useLocalePath()
const { t } = useI18n()
const { tenant } = useTenantTheme()

// Longest word of the final headline. EN/DE wrap at their space, but a
// single long word (PT "MERGULHE.", ES "SUMÉRGETE.") can't, so the CSS
// scales the font down by it instead of overflowing on phones.
const ctaWordChars = computed(() => Math.max(...t('landing_psy.final_title').split(/\s+/).map(w => w.length)))
// Same for the hero: "FREQUÊNCIA" / "FRECUENCIA" at the full 8rem are wider
// than the 900 px column and lost their last letters (the gradient paints
// only inside the box).
const heroWordChars = computed(() => Math.max(
  ...[t('landing_psy.hero_line_1'), t('landing_psy.hero_line_2')].join(' ').split(/\s+/).map(w => w.length)
))

// BPM ticker — ticks at 145 BPM (classic forest psy tempo)
const bpm = ref(0)
let bpmInterval = null
onMounted(() => {
  bpmInterval = setInterval(() => {
    bpm.value = (bpm.value + 1) % 1000
  }, Math.round(60000 / 145))
})
onBeforeUnmount(() => {
  if (bpmInterval) clearInterval(bpmInterval)
})

// Animated stat counters
const statsRef = ref(null)
const statsVisible = ref(false)

const stats = [
  { prefix: '', value: 150, suffix: '+', labelKey: 'landing_psy.stat_platforms' },
  { prefix: '', value: 48, suffix: 'h', labelKey: 'landing_psy.stat_delivery' },
  { prefix: '', value: 2500, suffix: '+', labelKey: 'landing_psy.stat_artists' },
]

const displayVals = reactive(stats.map(() => 0))

function animateStats() {
  const duration = 2200
  const start = performance.now()
  const step = (now) => {
    const p = Math.min((now - start) / duration, 1)
    const e = 1 - Math.pow(1 - p, 3)
    stats.forEach((s, i) => { displayVals[i] = Math.round(e * s.value) })
    if (p < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

let statsObserver = null
onMounted(() => {
  if (!statsRef.value) return
  statsObserver = new IntersectionObserver(
    ([entry]) => { if (entry.isIntersecting) { statsVisible.value = true; animateStats(); statsObserver?.disconnect() } },
    { threshold: 0.3 }
  )
  statsObserver.observe(statsRef.value)
})
onBeforeUnmount(() => statsObserver?.disconnect())

// Feature list — texts under landing_psy.feature_<key>_title/_desc
const features = [
  { key: 'distribution', glyph: '◎' },
  { key: 'analytics', glyph: '⬡' },
  { key: 'links', glyph: '◈' },
  { key: 'labels', glyph: '△' },
]
</script>

<style scoped>
/* ─── Page ─── */
.psy-page {
  background: var(--bg-color);
  color: var(--text-main);
  overflow-x: hidden;
  min-height: 100vh;
  font-family: var(--font-body);
}

/* ─── HERO ─── */
.psy-hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: clip;
}

/* The generated night-market plate, same image the shop uses. Sits at the
   very back of the hero with its own scrim so the orbs and the headline
   keep their contrast. */
.psy-hero-plate {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(
      to bottom,
      rgb(3 3 4 / 0.70) 0%,
      rgb(3 3 4 / 0.82) 55%,
      var(--bg-color) 100%
    ),
    url('/images/night-market-bg.webp');
  background-size: cover, cover;
  background-position: center top, center center;
  background-repeat: no-repeat, no-repeat;
}

.psy-hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

/* Animated orbs */
.psy-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(140px);
  animation: psyFloat 18s ease-in-out infinite;
}

.psy-orb-1 {
  width: 600px; height: 600px;
  background: var(--primary);
  opacity: 0.12;
  top: -15%; left: -10%;
  animation-delay: 0s;
}

.psy-orb-2 {
  width: 500px; height: 500px;
  background: var(--secondary);
  opacity: 0.10;
  top: 10%; right: -10%;
  animation-delay: -6s;
}

.psy-orb-3 {
  width: 400px; height: 400px;
  background: var(--accent);
  opacity: 0.07;
  bottom: 10%; left: 30%;
  animation-delay: -12s;
}

@keyframes psyFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(40px, -30px) scale(1.05); }
  66%       { transform: translate(-30px, 20px) scale(0.97); }
}

/* Scanline */
.psy-scanline {
  position: absolute;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(to right, transparent, rgb(var(--primary-channels) / 0.15), transparent);
  animation: scanline 8s linear infinite;
}

@keyframes scanline {
  0%   { top: -2px; opacity: 0; }
  5%   { opacity: 1; }
  95%  { opacity: 1; }
  100% { top: 100%; opacity: 0; }
}

/* Hero content */
.psy-hero-inner {
  position: relative;
  z-index: 2;
  text-align: center;
  /* 900 px of content plus the phone gutter — border-box would otherwise
     take the padding out of the headline's room. */
  max-width: calc(900px + 48px);
  margin-top: -60px;
  padding: 0 24px;
}

.psy-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--primary);
  margin-bottom: 32px;
  animation: fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both;
}

.psy-dot {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: var(--primary);
  box-shadow: 0 0 8px var(--primary);
  display: inline-block;
  animation: psyPulse 2s ease-in-out infinite;
}

@keyframes psyPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.3; transform: scale(0.7); }
}

.psy-hero-title {
  font-family: var(--font-heading);
  /* Shrinks for a long word (see heroWordChars) so it fits the column. */
  font-size: min(clamp(1.8rem, 11vw, 8rem), calc(min(100vw - 48px, 900px) / (var(--word-chars, 9) * 0.75)));
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.02em;
  margin-bottom: 32px;
  padding-bottom: 0.25em;
  overflow: visible;
  animation: fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both;
}

.psy-line-1 {
  display: block;
  line-height: 1.1;
  color: #fff;
  text-shadow: 0 0 60px rgb(var(--primary-channels) / 0.1);
}

.psy-line-2 {
  display: block;
  /* Give the gradient-masked line its own line-box tall enough for the Y
     descender — background-clip: text otherwise crops below the baseline. */
  line-height: 1.15;
  padding-bottom: 0.08em;
  /* Room for the last letter's overhang: the negative letter-spacing lets
     its ink reach past the text box, where background-clip: text paints
     nothing — the "Y" of FREQUENCY lost its right edge. Symmetric, so the
     line stays centred. */
  padding-inline: 0.08em;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 60%, var(--accent) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: none;
}

.psy-hero-sub {
  font-size: clamp(0.95rem, 2vw, 1.15rem);
  color: var(--text-muted);
  line-height: 1.8;
  max-width: 580px;
  margin: 0 auto 40px;
  animation: fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s both;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
}

.psy-hero-ctas {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  animation: fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.7s both;
}

/* BPM ticker */
.psy-bpm-ticker {
  position: absolute;
  top: 120px;
  right: 40px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  animation: fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 1s both;
}

.psy-bpm-label {
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: var(--primary);
  opacity: 0.6;
}

.psy-bpm-value {
  font-family: var(--font-heading);
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--primary);
  opacity: 0.4;
  font-variant-numeric: tabular-nums;
  min-width: 3ch;
  text-align: right;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Genre strip */
.psy-genre-strip {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 2;
  overflow: hidden;
  padding: 20px 0;
  border-top: 1px solid rgb(var(--primary-channels) / 0.08);
}

.psy-genre-fade {
  position: absolute;
  top: 0;
  width: 120px;
  height: 100%;
  z-index: 2;
  pointer-events: none;
}

.psy-genre-fade-l { left: 0; background: linear-gradient(to right, var(--bg-color), transparent); }
.psy-genre-fade-r { right: 0; background: linear-gradient(to left, var(--bg-color), transparent); }

.psy-genre-track { overflow: hidden; }

.psy-genre-content {
  display: flex;
  width: max-content;
  animation: psyMarquee 30s linear infinite;
}

.psy-genre-set {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-right: 20px;
}

.psy-genre-tag {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--primary);
  opacity: 0.45;
  white-space: nowrap;
  transition: opacity 0.2s;
}

.psy-genre-tag:hover { opacity: 1; }

.psy-genre-sep {
  color: var(--secondary);
  opacity: 0.25;
  font-size: 0.6rem;
}

@keyframes psyMarquee {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-33.333%); }
}

/* ─── STATS ─── */
.psy-stats {
  padding: 80px 24px;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  background: linear-gradient(
    to right,
    rgb(var(--primary-channels) / 0.02),
    rgb(var(--secondary-channels) / 0.02)
  );
}

.psy-stats-inner {
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  text-align: center;
}

.psy-stat-number {
  font-family: var(--font-heading);
  font-size: clamp(3rem, 7vw, 5.5rem);
  font-weight: 900;
  line-height: 1;
  color: var(--primary);
  text-shadow: 0 0 40px rgb(var(--primary-channels) / 0.3);
  margin-bottom: 12px;
  font-variant-numeric: tabular-nums;
}

.psy-stat-divider {
  width: 40px;
  height: 1px;
  background: var(--primary);
  margin: 0 auto 12px;
  opacity: 0.3;
}

.psy-stat-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--text-muted);
}

/* ─── MANIFESTO ─── */
.psy-manifesto {
  padding: 140px 24px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.psy-manifesto::before {
  content: '';
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 600px; height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle, rgb(var(--secondary-channels) / 0.06) 0%, transparent 70%);
  pointer-events: none;
}

.psy-manifesto-inner {
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 0 auto;
}

.psy-manifesto-symbol {
  font-size: 3rem;
  color: var(--secondary);
  opacity: 0.4;
  margin-bottom: 40px;
  animation: psySpin 20s linear infinite;
  display: inline-block;
}

@keyframes psySpin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.psy-quote {
  font-family: var(--font-heading);
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.2;
  color: #fff;
  margin: 0 0 32px;
  letter-spacing: -0.01em;
}

.psy-manifesto-body {
  font-size: 1.05rem;
  color: var(--text-muted);
  line-height: 1.85;
  max-width: 600px;
  margin: 0 auto;
}

/* ─── FEATURES ─── */
.psy-features {
  padding: 120px 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.psy-features-header {
  margin-bottom: 80px;
  text-align: center;
}

.psy-label-tag {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--primary);
  margin-bottom: 20px;
}

.psy-section-title {
  font-family: var(--font-heading);
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  line-height: 1.1;
  color: #fff;
}

.psy-section-title em {
  font-style: normal;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.psy-feature-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.psy-feature-row {
  display: grid;
  grid-template-columns: 80px 1fr 80px;
  align-items: center;
  gap: 40px;
  padding: 48px 0;
  border-bottom: 1px solid var(--border);
  transition: background 0.3s;
}

.psy-feature-row:hover {
  background: rgb(var(--primary-channels) / 0.02);
}

.psy-feature-row.reversed {
  direction: rtl;
}

.psy-feature-row.reversed > * { direction: ltr; }

.psy-feature-num {
  font-family: var(--font-heading);
  font-size: 3rem;
  font-weight: 900;
  color: var(--primary);
  opacity: 0.15;
  line-height: 1;
  transition: opacity 0.3s;
}

.psy-feature-row:hover .psy-feature-num { opacity: 0.5; }

.psy-feature-title {
  font-family: var(--font-heading);
  font-size: 1.6rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 12px;
}

.psy-feature-desc {
  font-size: 1rem;
  color: var(--text-muted);
  line-height: 1.7;
  max-width: 500px;
}

.psy-feature-glyph {
  font-size: 2rem;
  color: var(--secondary);
  opacity: 0.2;
  text-align: right;
  transition: opacity 0.3s;
}

.psy-feature-row:hover .psy-feature-glyph { opacity: 0.6; }

/* ─── PROCESS ─── */
.psy-process {
  padding: 120px 24px;
  border-top: 1px solid var(--border);
  text-align: center;
}

.psy-process-inner {
  max-width: 1000px;
  margin: 0 auto;
}

.psy-process .psy-label-tag { margin-bottom: 60px; display: block; }

.psy-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  position: relative;
}

.psy-step {
  padding: 0 40px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.psy-step:not(.last)::after {
  content: '';
  position: absolute;
  top: 30px;
  right: 0;
  width: 50%;
  height: 1px;
  background: linear-gradient(to right, var(--primary), transparent);
  opacity: 0.25;
}

.psy-step:not(:first-child)::before {
  content: '';
  position: absolute;
  top: 30px;
  left: 0;
  width: 50%;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--primary));
  opacity: 0.25;
}

.psy-step-circle {
  width: 60px; height: 60px;
  border-radius: 50%;
  border: 1px solid rgb(var(--primary-channels) / 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-heading);
  font-weight: 800;
  font-size: 0.9rem;
  color: var(--primary);
  margin-bottom: 28px;
  background: rgb(var(--primary-channels) / 0.05);
  box-shadow: 0 0 20px rgb(var(--primary-channels) / 0.1);
}

.psy-step-line { display: none; }

.psy-step-title {
  font-family: var(--font-heading);
  font-size: 1.3rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.psy-step-desc {
  font-size: 0.9rem;
  color: var(--text-muted);
  line-height: 1.6;
}

/* ─── CTA ─── */
.psy-cta {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-top: 1px solid var(--border);
}

.psy-cta-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.psy-cta-orb {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 700px; height: 700px;
  border-radius: 50%;
  background: radial-gradient(circle, rgb(var(--primary-channels) / 0.12) 0%, transparent 70%);
  animation: psyBreathe 6s ease-in-out infinite;
}

@keyframes psyBreathe {
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.8; }
  50%       { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
}

.psy-cta-ring {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 1px solid rgb(var(--primary-channels) / 0.08);
  animation: psyExpand 6s ease-out infinite;
}

.psy-ring-1 { width: 400px; height: 400px; animation-delay: 0s; }
.psy-ring-2 { width: 600px; height: 600px; animation-delay: 2s; }
.psy-ring-3 { width: 800px; height: 800px; animation-delay: 4s; }

@keyframes psyExpand {
  0%   { opacity: 0.4; transform: translate(-50%, -50%) scale(0.95); }
  50%  { opacity: 0.15; }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.05); }
}

.psy-cta-inner {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 24px;
}

.psy-cta-pre {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--primary);
  margin-bottom: 16px;
  opacity: 0.7;
}

.psy-cta-title {
  font-family: var(--font-heading);
  font-size: min(clamp(5rem, 15vw, 12rem), calc((100vw - 48px) / (var(--word-chars, 5) * 0.75)));
  font-weight: 900;
  line-height: 0.9;
  letter-spacing: -0.03em;
  color: #fff;
  text-shadow: 0 0 80px rgb(var(--primary-channels) / 0.2);
  margin-bottom: 32px;
}

.psy-cta-sub {
  font-size: 1.1rem;
  color: var(--text-muted);
  max-width: 480px;
  margin: 0 auto 48px;
  line-height: 1.7;
}

/* ─── Shared buttons ─── */
.psy-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 32px;
  background: var(--primary);
  color: #06040c;
  font-family: var(--font-heading);
  font-weight: 800;
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-radius: var(--radius-md);
  text-decoration: none;
  transition: all 0.2s;
  box-shadow: 0 0 30px rgb(var(--primary-channels) / 0.3);
}

.psy-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 0 50px rgb(var(--primary-channels) / 0.5);
}

.psy-btn-primary.psy-btn-large {
  height: 60px;
  padding: 0 48px;
  font-size: 0.85rem;
}

.psy-btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 32px;
  border: 1px solid rgb(var(--primary-channels) / 0.25);
  color: var(--primary);
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-radius: var(--radius-md);
  text-decoration: none;
  transition: all 0.2s;
  background: rgb(var(--primary-channels) / 0.04);
}

.psy-btn-ghost:hover {
  border-color: rgb(var(--primary-channels) / 0.5);
  background: rgb(var(--primary-channels) / 0.08);
}

/* ─── Responsive ─── */
@media (max-width: 768px) {
  .psy-hero-title { letter-spacing: -0.01em; }
  .psy-bpm-ticker { display: none; }
  .psy-hero-sub br { display: none; }

  .psy-stats-inner {
    grid-template-columns: repeat(2, 1fr);
    gap: 48px 24px;
  }

  .psy-manifesto { padding: 80px 24px; }
  .psy-quote { font-size: 2rem; }

  .psy-features { padding: 80px 24px; }
  .psy-feature-row {
    grid-template-columns: 48px 1fr 48px;
    gap: 20px;
    padding: 36px 0;
  }
  .psy-feature-row.reversed { direction: ltr; }
  .psy-feature-num { font-size: 2rem; }
  .psy-feature-title { font-size: 1.3rem; }
  .psy-feature-glyph { font-size: 1.5rem; }

  .psy-steps {
    grid-template-columns: 1fr;
    gap: 48px;
  }

  .psy-step::before,
  .psy-step::after { display: none; }

  .psy-cta-title { letter-spacing: -0.02em; }
}

@media (max-width: 480px) {
  .psy-stats-inner { grid-template-columns: 1fr; }
  .psy-hero-ctas { flex-direction: column; align-items: center; }
  .psy-btn-primary, .psy-btn-ghost { width: 100%; max-width: 280px; }
}
</style>
